// src/screens/mypage/StoreInfoScreen.js
// 72차 신규: 마이탭 "매장 관리" > "매장 정보" — 매장관리자가 자기 매장 요약(매장명·플랜·지점)을
// 확인하고 연락처(store.contact)를 셀프 수정할 수 있는 화면.
// 주소/지점 구성은 총괄 관리자 전용으로 남겨둠(위치 변경은 검토 없이 셀프수정하면 리스크가 있음) —
// firestore.rules상 isStoreAdmin()+managesStore()는 stores 문서 전체 update 권한이 있으므로
// contact 필드만 쓰는 건 별도 규칙 변경 없이 가능.
// 72차: 연락처 입력에 formatPhone() 자동 하이픈 삽입 추가(02-XXX-XXXX / 010-XXXX-XXXX)
// 72차: 저장 후 "저장됐어요" 메시지가 안 보이던 버그 수정 (아래 initializedForRef 참고)
// 72차: "일시 휴업" 토글 추가 — 총괄 관리자 전용인 isDeleted(완전 비활성화)와 달리
//       매장관리자가 스스로 켜고 끌 수 있는 임시 중단 상태. productService.js가
//       isPaused를 상품에 노출하고, 테마조회/테마상세에서 배지+예약 차단으로 반영됨.
// 72차: "알림 다이제스트" 토글 추가 — users/{uid}.notifDigest, 매장 선택과 무관한
//       계정 단위 설정. functions/index.js의 onNotificationCreated/
//       scheduledNotificationDigest가 이 값을 읽어 개별 푸시 대신 일일 요약을 보낸다.
// 75차: 자체 커스텀 헤더 → 공통 AppHeader로 교체(다른 화면과 동일 패턴 누락분)
// 78차 34차: 일시 휴업 Switch에 accessibilityLabel 추가 — 라벨 없는 스위치는 Maestro가
// 텍스트로 못 찾아 좌표 추정만 가능한데(다른 화면들에서 반복된 문제), 관리자 쓰기 동작
// 자동화(운영 상태 토글 플로우)에 필요해서 이번에 근본 수정.
// 78차: load()의 매장 순차 조회(for...of + await)를 Promise.all 병렬 조회로 변경 — 관리
// 매장이 여러 개인 매장관리자일수록 로딩이 매장 수만큼 선형으로 느려지던 문제
// 78차: "저장" 버튼(인라인 골드 fill)을 공용 GradientButton(Primary)으로 교체
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet,
  ScrollView, ActivityIndicator, Switch,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { PLAN_COLORS, normalizePlan, getPlanBadgeLabel } from '../../constants';
import { getStoreDisplayName, invalidateStoreCache } from '../../services/productService';
import { getBranchPlan, hasOpsAddon } from '../../services/feeService';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { MenuStoreIcon, IconLabel, MenuBellIcon, PhoneIcon } from '../../components/common/Icons';
import { COLORS } from '../../constants/theme';

const GOLD = COLORS.gold, BG = COLORS.bg, SURFACE = COLORS.surface, SURFACE2 = COLORS.surface2;
const BORDER = COLORS.border, TEXT = COLORS.text;
const TEXT_MUTED = COLORS.textMuted;
const GREEN = COLORS.green, RED = COLORS.red;

// 72차: 연락처 입력 시 "02-XXX(X)-XXXX" / "010-XXXX-XXXX" 형태로 자동 하이픈 삽입
const formatPhone = (value) => {
  const digits = value.replace(/[^0-9]/g, '').slice(0, 11);
  if (digits.startsWith('02')) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};

export default function StoreInfoScreen({ navigation }) {
  const insets = useSafeAreaInsets();
  const { user, profile, refreshProfile } = useAuth();
  const managedStoreIds = Array.isArray(profile?.managedStores)
    ? profile.managedStores
    : (profile?.managedStores ? [profile.managedStores] : []);

  const [stores,        setStores]        = useState({}); // storeId → data
  const [loading,       setLoading]       = useState(true);
  const [selectedId,    setSelectedId]    = useState(managedStoreIds[0] || null);
  const [contactInput,  setContactInput]  = useState('');
  const [saving,        setSaving]        = useState(false);
  const [msg,           setMsg]           = useState('');

  const load = useCallback(async () => {
    if (!managedStoreIds.length) { setLoading(false); return; }
    try {
      const snaps = await Promise.all(managedStoreIds.map(sid => getDoc(doc(db, 'stores', sid))));
      const results = {};
      snaps.forEach((snap, i) => {
        if (snap.exists()) results[managedStoreIds[i]] = { id: managedStoreIds[i], ...snap.data() };
      });
      setStores(results);
    } catch { /* 무음 처리 */ }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managedStoreIds.join(',')]);

  useEffect(() => { load(); }, [load]);

  const store = selectedId ? stores[selectedId] : null;

  // 72차 버그 수정: 이 effect가 store(객체 참조) 전체를 의존값으로 두고 있어서,
  // handleSave가 저장 직후 setStores로 store를 갱신하면 이 effect가 다시 돌면서
  // 방금 띄운 "저장됐어요" 메시지를 setMsg('')로 즉시 지워버렸다(저장은 됐는데
  // 안내가 안 보여 저장됐는지 알 수 없던 원인). store가 아니라 selectedId가 실제로
  // "바뀐" 시점(지점 전환/최초 로드)에만 입력값·메시지를 초기화하도록 분리.
  const initializedForRef = useRef(null);
  useEffect(() => {
    if (!store || initializedForRef.current === selectedId) return;
    initializedForRef.current = selectedId;
    setContactInput(store.contact ? formatPhone(store.contact) : '');
    setMsg('');
  }, [store, selectedId]);

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true); setMsg('');
    try {
      await updateDoc(doc(db, 'stores', selectedId), { contact: contactInput.trim() });
      // 80차 후속: [버그 수정 — write-then-display 재감사로 발견] 이 화면 자체는
      // setStores로 로컬 상태를 즉시 갱신해 문제없이 보이지만, 12초짜리 공용 캐시
      // (productService.js의 _rawStoresCache)를 안 비워서 그 창 안에 홈/예약탭 등
      // 고객 화면이 이 매장을 조회하면 최대 12초간 낡은 연락처를 보여줄 수 있었음 —
      // 같은 파일의 applyPause(휴업 토글)는 이미 하고 있던 것과 동일하게 맞춤.
      invalidateStoreCache();
      setStores(prev => ({ ...prev, [selectedId]: { ...prev[selectedId], contact: contactInput.trim() } }));
      setMsg('✅ 저장됐어요!');
    } catch (e) {
      setMsg('❌ 저장 실패: ' + e.message);
    } finally { setSaving(false); }
  };

  // 72차: 일시 휴업 토글 — 켜면 손님한테 새 예약을 못 받으니 확인 절차를 거친다
  const [pauseSaving, setPauseSaving] = useState(false);
  const applyPause = async (next) => {
    if (!selectedId) return;
    setPauseSaving(true);
    try {
      await updateDoc(doc(db, 'stores', selectedId), { isPaused: next });
      invalidateStoreCache();
      setStores(prev => ({ ...prev, [selectedId]: { ...prev[selectedId], isPaused: next } }));
    } catch (e) {
      showAlert('오류', '휴업 상태 변경에 실패했어요.\n' + e.message);
    } finally { setPauseSaving(false); }
  };
  const handleTogglePause = (next) => {
    if (next) {
      showAlert(
        '일시 휴업',
        '휴업을 시작하면 고객이 새로 예약할 수 없어요.\n이미 잡힌 예약은 그대로 유지됩니다. 계속할까요?',
        [
          { text: '취소', style: 'cancel' },
          { text: '휴업 시작', style: 'destructive', onPress: () => applyPause(true) },
        ]
      );
    } else {
      applyPause(false);
    }
  };

  // 72차: 알림 다이제스트 — 계정(user) 단위 설정이라 매장 선택과 무관하게 하나만 존재.
  // 켜면 새예약/새리뷰/예약취소 개별 푸시 대신 매일 21시 요약 알림 1건만 온다.
  const [digestSaving, setDigestSaving] = useState(false);
  const handleToggleDigest = async (next) => {
    if (!user?.uid) return;
    setDigestSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { notifDigest: next });
      await refreshProfile();
    } catch (e) {
      showAlert('오류', '알림 설정 변경에 실패했어요.\n' + e.message);
    } finally { setDigestSaving(false); }
  };

  if (loading) return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={s.headerTitle}>매장 정보</Text>} />
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={GOLD} />
      </View>
    </View>
  );

  return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={s.headerTitle}>매장 정보</Text>} />

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: insets.bottom + 32 }}>
        {!managedStoreIds.length ? (
          <Text style={{ color: TEXT_MUTED, textAlign: 'center', marginTop: 40 }}>
            담당 매장 정보가 없습니다. 총괄 관리자에게 문의해주세요.
          </Text>
        ) : (
          <>
            <View style={s.card}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flex: 1, marginRight: 12 }}>
                  <IconLabel Icon={MenuBellIcon} size={s.cardTitle.fontSize || 15} color={s.cardTitle.color} textStyle={s.cardTitle}>알림 다이제스트</IconLabel>
                  <Text style={s.hint}>
                    {profile?.notifDigest
                      ? '켜짐 — 새 예약·리뷰·취소 알림을 매일 21시에 한 번 요약으로 받아요.'
                      : '끄면 새 예약·리뷰·취소가 있을 때마다 바로 알림이 와요. 켜면 하루에 한 번 요약만 받아요.'}
                  </Text>
                </View>
                <Switch
                  value={!!profile?.notifDigest}
                  onValueChange={handleToggleDigest}
                  disabled={digestSaving}
                  trackColor={{ false: BORDER, true: GOLD + '80' }}
                  thumbColor={profile?.notifDigest ? GOLD : '#888'}
                />
              </View>
            </View>

            {managedStoreIds.length > 1 && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  {managedStoreIds.map(sid => (
                    <TouchableOpacity key={sid}
                      style={[s.chip, selectedId === sid && s.chipActive]}
                      onPress={() => setSelectedId(sid)}>
                      <Text style={[s.chipText, selectedId === sid && s.chipTextActive]}>
                        {getStoreDisplayName(stores[sid], sid)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            )}

            {store && (
              <>
                {/* 78차 68차: 사용자 요청으로 카드 순서 변경(알림 다이제스트 > 일시 휴업 >
                    연락처 > 매장 요약) */}
                <View style={s.card}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <View style={{ flex: 1, marginRight: 12 }}>
                      <Text style={s.cardTitle}>🚦 일시 휴업</Text>
                      <Text style={s.hint}>
                        {store.isPaused
                          ? '휴업 중이에요. 테마조회 화면에 "휴업중" 배지가 뜨고 새 예약이 막혀요.'
                          : '리모델링·휴가 등으로 예약을 잠시 막고 싶을 때 켜주세요.'}
                      </Text>
                    </View>
                    <Switch
                      value={!!store.isPaused}
                      onValueChange={handleTogglePause}
                      disabled={pauseSaving}
                      trackColor={{ false: BORDER, true: GOLD + '80' }}
                      thumbColor={store.isPaused ? GOLD : '#888'}
                      accessibilityLabel={store.isPaused ? '일시 휴업 해제' : '일시 휴업 시작'}
                    />
                  </View>
                </View>

                <View style={s.card}>
                  <IconLabel Icon={PhoneIcon} size={s.cardTitle.fontSize || 15} color={s.cardTitle.color} textStyle={s.cardTitle}>연락처</IconLabel>
                  <TextInput
                    style={s.input}
                    value={contactInput}
                    onChangeText={v => setContactInput(formatPhone(v))}
                    placeholder="고객에게 보여줄 연락처 (예: 010-1234-5678)"
                    placeholderTextColor={TEXT_MUTED}
                    keyboardType="phone-pad"
                  />
                  {msg ? (
                    <Text style={{ color: msg.startsWith('✅') ? GREEN : RED, fontSize: 12, marginTop: 8 }}>{msg}</Text>
                  ) : null}
                  <GradientButton
                    label={saving ? '저장 중...' : '💾 저장'}
                    onPress={handleSave}
                    disabled={saving}
                    style={s.saveBtn}
                  />
                </View>

                <View style={s.card}>
                  <IconLabel Icon={MenuStoreIcon} size={14} color={GOLD} textStyle={s.cardTitle}>매장 요약</IconLabel>
                  <View style={s.rowLine}>
                    <Text style={s.rowLabel}>매장명</Text>
                    <Text style={s.rowValue}>{getStoreDisplayName(store)}</Text>
                  </View>
                  {/* 78차 68차: store.plan(최상위 필드) 하나만 보여주고 있어서, 지점마다
                      플랜이 다른 매장(솔버 건대1호점/2호점 등)도 항상 같은 값으로 표시되던
                      버그 — 지점별로 실제 plan(getBranchPlan)을 각각 계산해 나열. */}
                  {(store.branches || []).map((b, bi, arr) => {
                    const bPlan = getBranchPlan(store, bi);
                    const isLast = bi === arr.length - 1;
                    return (
                      <View key={bi} style={[s.rowLine, isLast && { borderBottomWidth: 0 }]}>
                        <Text style={s.rowLabel}>{b.branchName || `지점 ${bi + 1}`}</Text>
                        <Text style={[s.rowValue, { color: PLAN_COLORS[bPlan], fontWeight: '800' }]}>
                          {getPlanBadgeLabel(bPlan, hasOpsAddon(store, bi))}
                        </Text>
                      </View>
                    );
                  })}
                  {(store.branches || []).length === 0 && (
                    <View style={[s.rowLine, { borderBottomWidth: 0 }]}>
                      <Text style={s.rowLabel}>플랜</Text>
                      <Text style={[s.rowValue, { color: PLAN_COLORS[normalizePlan(store.plan)], fontWeight: '800' }]}>
                        {getPlanBadgeLabel(store.plan, hasOpsAddon(store))}
                      </Text>
                    </View>
                  )}
                  <Text style={s.hint}>
                    매장명·플랜·지점 구성은 총괄 관리자만 변경할 수 있어요. 변경이 필요하면 총괄 관리자에게 문의해주세요.
                  </Text>
                </View>
              </>
            )}
          </>
        )}
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  container:    { flex: 1, backgroundColor: BG },
  headerTitle:  { color: TEXT, fontSize: 15, fontWeight: '800' },

  chip:         { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 18,
                  borderWidth: 1, borderColor: BORDER, backgroundColor: SURFACE2 },
  chipActive:   { borderColor: GOLD, backgroundColor: GOLD + '18' },
  chipText:     { color: TEXT_MUTED, fontSize: 13 },
  chipTextActive: { color: GOLD, fontWeight: '800' },

  card:         { backgroundColor: SURFACE, borderRadius: 14, borderWidth: 1, borderColor: BORDER,
                  padding: 16, marginBottom: 14 },
  cardTitle:    { color: GOLD, fontSize: 14, fontWeight: '800', marginBottom: 10 },
  rowLine:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
                  paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.06)' },
  rowLabel:     { color: TEXT_MUTED, fontSize: 12 },
  rowValue:     { color: TEXT, fontSize: 13, fontWeight: '700', flexShrink: 1, textAlign: 'right', marginLeft: 12 },
  hint:         { color: TEXT_MUTED, fontSize: 11, marginTop: 10, lineHeight: 16 },

  input:        { backgroundColor: SURFACE2, borderWidth: 1, borderColor: BORDER,
                  borderRadius: 8, paddingHorizontal: 12, height: 44, color: TEXT, fontSize: 14 },
  saveBtn:      { marginTop: 12 },
});
